import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import * as moment from 'moment';
import {
  IUpcomingAppointmentsDS,
  UpcomingAppointmentsDatasource
} from '../datasources/upcoming_appointments_datasource';
import { IActiveVisit, IAppointment, IAppointmentRequest } from '../types/appointment_interfaces';

const OVERDUE_TXT = 'Cancelled: ';
export class UpcomingAppointmentsQuery implements IQuery {
  private _result: IUpcomingAppointmentsDS;

  constructor(QueryMediator: IQueryMediator, private ngUpcomingAppointmentsDatasource: UpcomingAppointmentsDatasource) {
    QueryMediator.registerQuery(this, ngUpcomingAppointmentsDatasource);
  }

  private _formatAppointmentContentTile(appt: IAppointment) {
    const attendees = appt.attendees.length - 1;
    const title =
      attendees > 0 ? `${appt.student_services[0].name} + ${attendees} more` : appt.student_services[0].name;
    const subTitle = `${moment.unix(appt.start_timestamp).format('ddd, MMM D [at] h:mm a')}`;
    return {
      icon: { type: 'calender', theme: 'magenta' },
      text: {
        title,
        subTitle,
        overdueText: OVERDUE_TXT
      },
      action: { state: 'my.myAppointments.detail', params: { id: appt.attendance_id } },
      isOverdue: appt.is_cancelled,
      isActive: true
    };
  }

  private _formatAppointmentRequest(appt: IAppointmentRequest) {
    const title = `Appointment Request for ${appt.student_service.name}`;
    const organizer = `${appt.organizers[0].first_name} ${appt.organizers[0].last_name}`;
    const subTitle = `Schedule your appointment by ${moment(appt.end_date).format('ddd, MMM D')} with ${organizer}`;
    return {
      icon: { type: 'calender', theme: 'magenta' },
      text: {
        title,
        subTitle,
        overdueText: OVERDUE_TXT
      },
      action: { state: 'my.myAppointments.request', params: { id: appt.id } },
      isOverdue: false,
      isActive: true
    };
  }

  private _formatActiveAppointment(appt: IActiveVisit) {
    const title = `${_.map(_.get(appt, 'student_services')).join(', ')}`;
    const subTitle = `Checked in at ${moment(appt.check_in_time).format('ddd, MMM D h:mm a')}`;
    const appointment = appt.attendance_id ? this.findAppointment(appt.attendance_id.toString()) : {};
    return {
      icon: { type: 'calender', theme: 'magenta' },
      text: {
        title,
        subTitle,
        overdueText: OVERDUE_TXT
      },
      action: { state: 'my.myAppointments.detail', params: { id: appt.id, activeVisit: true } },
      isOverdue: _4.get(appointment, 'is_cancelled', false),
      isActive: true
    };
  }

  __mapActiveApptToContentTile(appts: any[]) {
    return appts.map((appt) => {
      return {
        ...appt,
        _contentTile: this._formatActiveAppointment(appt)
      };
    });
  }

  __mapApptToContentTile(appts: any[]) {
    const allActiveVisits = this.ngUpcomingAppointmentsDatasource.read().activeVisits;

    return appts.map((appt) => {
      const _activeVisit: IActiveVisit = _4.find(allActiveVisits, { attendance_id: appt.attendance_id });
      return {
        ...appt,
        _activeVisit,
        _canCheckin: _4.get(appt, 'can_checkin', false),
        _canCheckout: _4.get(_activeVisit, 'can_checkout', false),
        _contentTile: this._formatAppointmentContentTile(appt)
      };
    });
  }

  run(): UpcomingAppointmentsQuery {
    this._result = this.ngUpcomingAppointmentsDatasource.read();
    this._result.upcomingAppointments = this.__mapApptToContentTile(this._result.upcomingAppointments);
    this._result.pastAppointments = this.__mapApptToContentTile(this._result.pastAppointments);
    this._result.activeVisits = this.__mapActiveApptToContentTile(this._result.activeVisits);
    this._result.appointmentRequests = this._result.appointmentRequests.map((appt) => {
      return {
        ...appt,
        _contentTile: this._formatAppointmentRequest(appt)
      };
    });
    return this;
  }

  findAppointment(id: string): any {
    const appts = [...this._result.pastAppointments, ...this._result.upcomingAppointments];
    const appt = _4.find(appts, (a: IAppointment) => {
      return parseInt(`${a.attendance_id}`, 10) === parseInt(id, 10);
    });
    if (!_4.isNil(appt)) {
      return appt;
    }
    return _4.find(this._result.appointmentRequests, (a: IAppointmentRequest) => {
      return parseInt(`${a.id}`, 10) === parseInt(id, 10);
    });
  }

  findActiveVisit(id: string): IActiveVisit {
    const visits = [...this._result.activeVisits];
    return _4.find(visits, (a: IActiveVisit) => {
      return parseInt(`${a.id}`, 10) === parseInt(id, 10);
    });
  }

  result(showNextMonthsAppointmentsOnly?: boolean): any {
    if (showNextMonthsAppointmentsOnly) {
      this._result.upcomingAppointments = _.take<IAppointment>(
        _.filter<IAppointment>(this._result.upcomingAppointments, (appointment: IAppointment): boolean => {
          const startTime: moment.Moment = moment.unix(appointment.start_timestamp);
          return startTime.diff(moment(), 'days') <= 30;
        }),
        5
      );
    }

    return this._result;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-appointments/src/queries/upcoming_appointments_query.ts